import React from "react"
import ReactDOM from "react-dom/client"
import "./main.css"

import { router } from "./lib/router"
import { RouterProvider } from "@tanstack/react-router"
import { QueryClientProvider } from "@tanstack/react-query"
import { queryClient } from "./lib/react-query"
import { Settings } from "luxon"
import { client } from "./lib/ts-rest"
import { trackEvent } from "./lib/log"
import ReactGA from "react-ga4"
import posthog from "posthog-js"

import * as Sentry from "@sentry/react"
import { isLmsWindow } from "./lib/lms"

Settings.throwOnInvalid = true
declare module "luxon" {
  interface TSSettings {
    throwOnInvalid: true
  }
}

// ENG-1476: the LMS launch window serves customers' learners in a scoped
// session. It runs no session recording, product analytics or performance
// tracing (Sentry Replay runs with text unmasked); only error reports go to
// Sentry. LMS activity is still tracked server-side by the API.
const isLms = isLmsWindow()

Sentry.init({
  dsn: "https://9cf5647afba444e204d8da182279d10d@o4504333520535552.ingest.us.sentry.io/4507973790728192",
  integrations: [
    ...(isLms ? [] : [Sentry.browserTracingIntegration()]),
    ...(isLms
      ? []
      : [
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
            maskAllInputs: false,
          }),
        ]),
  ],
  // Tracing
  tracesSampleRate: isLms ? 0 : 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: isLms ? 0 : 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: isLms ? 0 : 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  environment: import.meta.env.VITE_APP_ENV,
  // Release version for source map linking (set during CI/CD build)
  release: import.meta.env.VITE_SENTRY_RELEASE,
})

// Initialize PostHog analytics (skip in automated test environments like Playwright to prevent test data contamination, and in the LMS launch window)
if (!navigator.webdriver && !isLms) {
  posthog.init("phc_mpnLmrdeoBPOs7LRMAq90UvxS7I8KVpMoDhG9mZb8vQ", {
    api_host: "https://eu.i.posthog.com",
    // Record request URLs, statuses and timings in the replay's network tab.
    // A failed call start (ENG-1546) reaches us as a bare `TypeError: Failed
    // to fetch` with no status code, because the request never produced an
    // HTTP response. The only client-side evidence of WHICH host the browser
    // could not reach is the request entry itself, sitting next to the
    // requests that did succeed. Console capture (already on project-wide)
    // does not help here: the browser's `net::ERR_*` line is emitted by the
    // renderer, not through the console API posthog-js patches.
    capture_performance: { network_timing: true },
    session_recording: {
      // Headers and bodies stay off, since call payloads carry scenario
      // content and auth tokens, and the URL + status + duration is the whole
      // diagnosis.
      recordHeaders: false,
      recordBody: false,
    },
  })
}

if (!isLms) {
  ReactGA.initialize("G-1S5HZ66DKY", {
    gtagOptions: { send_page_view: false },
  })
}

// router.history.subscribe(() => {
//   const href = router.history.location.href
//   console.log(href)
//   ReactGA.send({
//     hitType: "pageview",
//     page: href,
//     title: href,
//   })
// })

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <client.ReactQueryProvider>
        <RouterProvider router={router} />
      </client.ReactQueryProvider>
    </QueryClientProvider>
  </React.StrictMode>
)
